import { Link } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/core";

export interface CartEmptyProps {
  /** Se llama al ir al catálogo: el panel se cierra antes de navegar. */
  onGoToShop: () => void;
}

/**
 * Carrito vacío.
 *
 * ── Por qué NO es un panel en blanco ─────────────────────────────────
 * Un cajón vacío que se abre y no dice nada se lee como una avería: la
 * primera reacción es volver a pulsar el carrito a ver si esta vez carga.
 * Aquí hay una frase que explica el estado y una salida — el catálogo —
 * que es lo único que se puede hacer desde aquí.
 *
 * ── Y por qué no parece un error ─────────────────────────────────────
 * Nada de rojo ni de icono de alerta. Un carrito vacío es el estado
 * NORMAL de quien acaba de llegar.
 *
 * El botón es un enlace de verdad (`as={Link}`), no un `<button>` que
 * llama a `navigate()`: así funcionan el clic con rueda, el "abrir en
 * pestaña nueva" y el menú contextual, y el lector lo anuncia como enlace.
 */
export function CartEmpty({ onGoToShop }: CartEmptyProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-12 text-center">
      <span className="mb-5 flex h-16 w-16 items-center justify-center rounded-ds-lg border border-ds-lavender-bg-2 bg-ds-mist/40">
        <ShoppingBag className="h-7 w-7 text-ds-lavender" aria-hidden="true" />
      </span>

      <p className="text-[15px] font-semibold text-ds-ink">
        {t("Tu carrito está vacío")}
      </p>

      <p className="mx-auto mt-2 max-w-xs text-[13px] leading-relaxed text-ds-ink-3">
        {t("Explora el catálogo y añade lo que quieras llevarte.")}
      </p>

      <Button as={Link} to="/shop" onClick={onGoToShop} variant="dark" size="sm" className="mt-6">
        {t("Ver el catálogo")}
      </Button>
    </div>
  );
}
